/**
 * JSON-LD builders for per-page schema nodes appended to the <SEOHead> @graph.
 * Article schema for blog posts, FAQPage schema for FAQ lists.
 */

const ORIGIN = 'https://trainzilla.app';

export type ArticleSchemaInput = {
  title: string;
  description: string;
  slug: string; // e.g. "ai-fitness-revolution" -> /blog/ai-fitness-revolution
  author?: string;
  authorBio?: string;
  /** Free-text date as authored ("January 10, 2025"); best-effort parsed to ISO. */
  date?: string;
  image?: string;
  section?: string;
  tags?: string[];
};

function toIso(date?: string): string | undefined {
  if (!date) return undefined;
  const t = Date.parse(date);
  if (Number.isNaN(t)) return undefined;
  // Use local date parts so an authored "January 10, 2025" doesn't shift a day
  // across the UTC boundary at build time.
  const d = new Date(t);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function buildArticleSchema(a: ArticleSchemaInput): Record<string, unknown> {
  const url = `${ORIGIN}/blog/${a.slug}`;
  const published = toIso(a.date);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: a.title,
    description: a.description,
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    ...(a.image ? { image: [a.image] } : {}),
    ...(published ? { datePublished: published, dateModified: published } : {}),
    author: {
      '@type': 'Person',
      name: a.author || 'TrainZilla',
      ...(a.authorBio ? { description: a.authorBio } : {}),
    },
    publisher: {
      '@type': 'Organization',
      name: 'TrainZilla',
      logo: { '@type': 'ImageObject', url: `${ORIGIN}/og-image.png` },
    },
    ...(a.section ? { articleSection: a.section } : {}),
    ...(a.tags?.length ? { keywords: a.tags.join(', ') } : {}),
    inLanguage: 'en',
  };
}

export type FaqItem = { question: string; answer: string };

export function buildFaqSchema(items: FaqItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}
